import { useSyncExternalStore } from 'react';
import type {
  AccountView,
  DebriefView,
  HistoryItem,
  MissionBrief,
  TerminalSnapshot
} from '@head-of-trading/contracts';
import type { MissionCatalogItem } from '../lib/api.js';

export type AppScreen = 'login' | 'missions' | 'brief' | 'terminal' | 'debrief' | 'history';
export type OrderEntryMode = 'classic' | 'quick';
export type BottomTab = 'positions' | 'orders' | 'fills';
export type CenterTab = 'chart' | 'book';

export interface AppState {
  readonly introVisible: boolean;
  readonly screen: AppScreen;
  readonly account: AccountView | null;
  readonly missions: readonly MissionCatalogItem[];
  readonly selectedMission: MissionCatalogItem | null;
  readonly brief: MissionBrief | null;
  readonly attemptId: string | null;
  readonly snapshot: TerminalSnapshot | null;
  readonly debrief: DebriefView | null;
  readonly history: readonly HistoryItem[];
  readonly busy: boolean;
  readonly error: string | null;
  readonly selectedSymbol: string;
  readonly orderMode: OrderEntryMode;
  readonly quickArmed: Readonly<Record<string, boolean>>;
  readonly quickQuantities: Readonly<Record<string, number>>;
  readonly bottomTab: BottomTab;
  readonly centerTab: CenterTab;
  readonly riskOpen: boolean;
  readonly helpOpen: boolean;
  readonly calculatorOpen: boolean;
  readonly briefOpen: boolean;
  readonly fomcOpen: boolean;
  readonly chartType: 'line' | 'candles';
  readonly showTradeMarkers: boolean;
  readonly tradingShortcuts: boolean;
  readonly lastSeq: number;
  readonly takeoverAccount: AccountView | null;
  readonly controlRevoked: boolean;
}

const initialState: AppState = {
  introVisible: true,
  screen: 'login', account: null, missions: [], selectedMission: null, brief: null,
  attemptId: null, snapshot: null, debrief: null, history: [], busy: false, error: null,
  selectedSymbol: '', orderMode: 'classic', quickArmed: {}, quickQuantities: {}, bottomTab: 'positions', centerTab: 'chart',
  riskOpen: false, helpOpen: false, calculatorOpen: false, briefOpen: false, fomcOpen: false,
  chartType: 'candles', showTradeMarkers: true, tradingShortcuts: false, lastSeq: 0,
  takeoverAccount: null, controlRevoked: false
};

type Listener = () => void;
let state: AppState = initialState;
const listeners = new Set<Listener>();

export const appStore = {
  getState: () => state,
  subscribe(listener: Listener) { listeners.add(listener); return () => listeners.delete(listener); },
  set(update: Partial<AppState> | ((current: AppState) => Partial<AppState>)) {
    const patch = typeof update === 'function' ? update(state) : update;
    state = { ...state, ...patch };
    for (const listener of listeners) listener();
  },
  resetForAccount(account: AccountView) {
    state = { ...initialState, introVisible: false, account, screen: 'missions' };
    for (const listener of listeners) listener();
  },
  resetTerminalUi(snapshot: TerminalSnapshot) {
    const quickQuantities = Object.fromEntries(snapshot.instruments.map((instrument) => [instrument.symbol, 1]));
    state = {
      ...state, snapshot, attemptId: snapshot.attemptId, selectedSymbol: snapshot.selectedSymbol,
      quickArmed: {}, quickQuantities, orderMode: 'classic', bottomTab: 'positions', centerTab: 'chart',
      riskOpen: typeof window !== 'undefined' && window.innerWidth >= 2_560 && window.innerHeight >= 1_440,
      helpOpen: false, calculatorOpen: false, briefOpen: false, fomcOpen: false,
      screen: 'terminal', debrief: null, lastSeq: 0, error: null, controlRevoked: false
    };
    for (const listener of listeners) listener();
  }
};

export function useAppStore<T>(selector: (value: AppState) => T): T {
  const snapshot = useSyncExternalStore(appStore.subscribe, appStore.getState, appStore.getState);
  return selector(snapshot);
}

export function saveSession(account: AccountView, attemptId: string | null): void {
  try { sessionStorage.setItem('hot.session', JSON.stringify({ account, attemptId })); } catch { /* session recovery is best effort */ }
}

export function loadSession(): { account: AccountView; attemptId: string | null } | null {
  try {
    const raw = sessionStorage.getItem('hot.session');
    if (!raw) return null;
    const value = JSON.parse(raw) as { account?: AccountView; attemptId?: string | null };
    return value.account ? { account: value.account, attemptId: value.attemptId ?? null } : null;
  } catch { return null; }
}

export function clearSession(): void {
  try { sessionStorage.removeItem('hot.session'); } catch { /* no-op */ }
}
